import { useEffect, useRef, useState } from "react"
import { isUndefined } from "lodash"
import { useTranslation } from "react-i18next"
import {
  HR_BLOCKED_KEYS,
  isAllowedZoneCellInput,
} from "@/app/settings/metric/zones/components/validation"

import TextField from "@/components/form/textField/TextField"
import IconButton from "@/components/button/IconButton"
import { Item } from "@/types/rangeInput"
import { validateRangeInput, hasRangeInputError } from "@/utils/range"

import "./rangeInput.scss"

// TODO 로직 보강 필요
export default function RangeInput({
  items,
  setItems,
  value,
  setValue,
  step = 0.1,
  onError,
  ...props
}: {
  items: Item[]
  setItems: (items: Item[]) => void
  value: Array<string | number>
  setValue: (value: Array<string | number>) => void
  min: number
  max: number
  minCount: number
  maxCount: number
  step?: number
  onError?: (hasError: boolean) => void
}) {
  const [itemList, setItemList] = useState<Item[]>([])

  useEffect(() => {
    setItemList(items)
  }, [items])

  // 에러 상태 계산 및 부모에게 전달
  useEffect(() => {
    if (!onError) return

    const hasError = items.some((item) =>
      item.value.some((itemValue, i) => {
        const valueIndex = Number(itemValue)
        // readonly나 disabled인 필드는 체크하지 않음
        if (item.disabled[i] || item.readonly[i]) return false
        // 유효한 인덱스가 아니면 체크하지 않음
        if (valueIndex < 0 || valueIndex > value.length - 1) return false

        return hasRangeInputError(value[valueIndex], valueIndex, value, props.min, props.max, step)
      }),
    )

    onError(hasError)
  }, [items, value, onError, props.min, props.max, step])

  const handleRemoveItem = () => {
    const updatedItems = [...items]
    updatedItems.pop() // 가장 마지막 아이템 삭제
    const newUpdatedItems = updatedItems.map((item, index) => {
      if (index === updatedItems.length - 1) {
        return {
          ...item,
          value: [item.value[0]],
        }
      }

      return item
    }) // 마지막 -1 번째 아이템 중 마지막 value 삭제
    setItems(newUpdatedItems)

    // value 조절
    setValue(value.slice(0, value.length - 1))
  }

  const handleAddItem = () => {
    const updatedItems = [...items]
    const newUpdatedItems = updatedItems.map((item, index) => {
      if (index === updatedItems.length - 1) {
        return {
          ...item,
          value: [item.value[0], Number(item.value[0]) + 1],
        }
      }

      return item
    }) // 마지막 아이템 value 추가
    newUpdatedItems.push({
      active: [true],
      label: updatedItems[updatedItems.length - 1].label.replace(/\d+/, (num) =>
        String(Number(num) + 1),
      ),
      value: [Number(newUpdatedItems[newUpdatedItems.length - 1].value[0]) + 1],
      disabled: [true],
      readonly: [true],
    }) // 가장 마지막 아이템 추가
    setItems(newUpdatedItems)

    // value 조절
    setValue([...value, "0"])
  }

  return (
    <div className="RangeInputCont">
      {itemList.map((item, index) => (
        <RangeInputItem
          key={`item-${index}`}
          index={index}
          item={item}
          itemList={itemList}
          value={value}
          setValue={setValue}
          handleAddItem={handleAddItem}
          handleRemoveItem={handleRemoveItem}
          step={step}
          {...props}
        />
      ))}
    </div>
  )
}

function RangeInputItem({
  index,
  item,
  itemList,
  value,
  setValue,
  handleRemoveItem,
  handleAddItem,
  step = 1,
  min,
  max,
  minCount,
  maxCount,
  hideLabel,
  hideBtns,
}: {
  index: number
  item: Item
  itemList: Item[]
  value: Array<string | number>
  setValue: (value: Array<string | number>) => void
  handleRemoveItem: (index: number) => void
  handleAddItem: (index: number) => void
  min: number
  max: number
  step?: number
  minCount?: number
  maxCount?: number
  hideLabel?: boolean
  hideBtns?: boolean
}) {
  const ref = useRef<HTMLDivElement>(null)

  const { t } = useTranslation()

  const getErrMessage = (inputValue: string | number, i: number) => {
    const { errorType, newMin, newMax } = validateRangeInput(inputValue, i, value, min, max, step)
    const name = item.label.split("(")[0]

    switch (errorType) {
      case "empty":
        return t("rules.required")
      case "notNumeric":
        return t("rules.numeric", { name })
      case "min":
        return t("rules.min", { name, min: newMin })
      case "max":
        return t("rules.max", { name, max: newMax })
      default:
        return ""
    }
  }

  return (
    <div key={`item-${index}`} className="RangeInputItemCont" ref={ref}>
      {!hideLabel && <label className="label">{item.label}</label>}
      <div className="range-input-item">
        {item.value.map((itemValue, i) => {
          const valueIndex = Number(itemValue)

          return (
            <div key={`item-value-${i}`} className="range">
              {valueIndex >= 0 && valueIndex <= value.length - 1 ? (
                <TextField
                  className="range-input-text-field"
                  value={
                    step === 1 ? Number(value[valueIndex]).toFixed(0) : (value[valueIndex] ?? "")
                  }
                  // HR(step 1)은 정수만 — 소수점 키를 막고 소수가 섞인 입력은 반영하지 않는다 (버그 #7)
                  onKeyDown={(e) =>
                    step === 1 && HR_BLOCKED_KEYS.includes(e.key) && e.preventDefault()
                  }
                  onChange={(e) => {
                    if (step === 1 && !isAllowedZoneCellInput("hr", e.target.value)) {
                      return
                    }
                    const newValue = [...value]
                    newValue[valueIndex] = e.target.value
                    setValue(newValue)
                  }}
                  onBlur={() => {
                    // required / numeric 에러처리를 위해
                    if (value[valueIndex] !== "") {
                      setValue(value.map((v) => Math.round(Number(v) * 10) / 10))
                    } else if (step === 1) {
                      setValue(value.map((v) => Number(v).toFixed(0)))
                    }
                  }}
                  slotProps={{
                    input: {
                      readOnly: item.disabled[i] || item.readonly[i],
                    },
                  }}
                  error={
                    !item.disabled[i] &&
                    !item.readonly[i] &&
                    !!getErrMessage(value[valueIndex], valueIndex)
                  }
                  helperText={
                    !item.disabled[i] &&
                    !item.readonly[i] &&
                    getErrMessage(value[valueIndex], valueIndex)
                  }
                  inputProps={{ step: step }}
                  type="number"
                />
              ) : (
                <TextField
                  className="last-text-field range-input-text-field"
                  value={0}
                  slotProps={{
                    input: {
                      readOnly: item.disabled[i] || item.readonly[i],
                    },
                  }}
                />
              )}
              {i % 2 === 0 && <span className="tilde-sign">~</span>}
            </div>
          )
        })}

        {index === itemList.length - 1 && !hideBtns && (
          <div className="RangeInputButtonCont">
            <IconButton
              type="default"
              size="md"
              disabled={!isUndefined(minCount) && minCount >= index + 1}
              onClick={() => handleRemoveItem(index)}
              icon="subtract"
            />
            <IconButton
              type="default"
              size="md"
              disabled={!isUndefined(maxCount) && maxCount === index + 1}
              onClick={() => handleAddItem(index)}
              icon="add"
            />
          </div>
        )}
      </div>
    </div>
  )
}
